import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Network, Plus, Edit, Trash, Users, Maximize2, Filter, AlertTriangle } from "lucide-react";
import { Select } from "antd";
import { OrgChart } from "d3-org-chart";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { DialogModal, ConfirmPopupModal } from "../../components/ui/dialogModal";
import ToastMsg from "../../components/common/ToastMsg";
import { checkUserPermissionFunc } from "../../components/common/CommonFunctions";
import useApiMethods from "../../lib/useApiMethods";
import {
    SUBORDINATE_SETUP,
    SUBORDINATE_CARDS,
    EMPLOYEE_PERSONAL_INFO,
    EMPLOYEE_DEPARTMENT,
    EMPLOYEE_DESIGNATION,
} from "../../constants/api";

// Default colors offered by the legacy UI for card tags — kept as a palette
// for the picker.
const COLOR_PALETTE = ["#D94854", "#0E862A", "#EF7A55", "#616161", "#CACACA", "#4C6EF5", "#A855F7", "#0EA5E9"];

// Backend stores colour without the leading `#` sometimes — normalize for CSS.
const ensureHash = (c) => (c ? (c.startsWith("#") ? c : `#${c}`) : "");

// ---------------------------------------------------------------------------
// Tree helpers — backend gives us a nested tree; d3-org-chart consumes a flat
// list with {id, parentId}. Convert both directions so the rest of the code
// can stay tree-shaped (matches the API).
// ---------------------------------------------------------------------------
const flattenTree = (root) => {
    const out = [];
    if (!root) return out;
    // d3.stratify rejects the whole tree if any id repeats. Even one bad row
    // would crash the page, so we silently skip duplicates here as a second
    // line of defense — the backend is the primary guard.
    const seen = new Set();
    const walk = (node, parentId) => {
        if (!node || node.node_id == null) return;
        const id = String(node.node_id);
        if (seen.has(id)) return;
        seen.add(id);
        out.push({
            id,
            parentId: parentId ? String(parentId) : "",
            // Pass the whole node along so the node template can read everything.
            _node: node,
        });
        (node.subordinates || []).forEach((c) => walk(c, node.node_id));
    };
    walk(root, null);
    return out;
};

const findNodeByNodeId = (root, nodeId) => {
    if (!root) return null;
    if (String(root.node_id) === String(nodeId)) return root;
    for (const s of root.subordinates || []) {
        const found = findNodeByNodeId(s, nodeId);
        if (found) return found;
    }
    return null;
};

// Forest-level lookups — we now render multiple super-admin roots side by side.
const findNodeInForest = (roots, nodeId) => {
    for (const r of roots || []) {
        const found = findNodeByNodeId(r, nodeId);
        if (found) return found;
    }
    return null;
};

const findOwningRootIndex = (roots, nodeId) => {
    for (let i = 0; i < (roots || []).length; i += 1) {
        if (findNodeByNodeId(roots[i], nodeId)) return i;
    }
    return -1;
};

// Pull a node (and its subtree) out of the parent. Returns
// { detached, remainingTree } so drag-and-drop can splice the subtree under
// a new parent without losing any state.
const detachNode = (root, targetNodeId) => {
    let detached = null;
    const walk = (n) => {
        if (!n) return null;
        if (String(n.node_id) === String(targetNodeId)) {
            detached = n;
            return null;
        }
        return {
            ...n,
            subordinates: (n.subordinates || []).map(walk).filter(Boolean),
        };
    };
    const remainingTree = walk(root);
    return { detached, remainingTree };
};

// Return a deep clone of `root` with the node matching `targetNodeId` passed
// through `mutator`. Used for every save so the PUT body always contains the
// full tree — the backend's add_subordinates walks recursively and sets
// reporting_manager_id from the parent; PUTting only a subtree would orphan
// the node (its reporting_manager_id would reset to NULL).
const replaceNodeInTree = (root, targetNodeId, mutator) => {
    if (!root) return null;
    if (String(root.node_id) === String(targetNodeId)) {
        const replaced = mutator(root);
        return replaced || null;
    }
    return {
        ...root,
        subordinates: (root.subordinates || [])
            .map((s) => replaceNodeInTree(s, targetNodeId, mutator))
            .filter(Boolean),
    };
};

// Drop every is_active=false branch entirely so the chart reflects "removed
// from hierarchy" instead of the legacy "greyed-out tombstone" look. Backend
// keeps the row for audit; the UI just chooses not to draw it.
const filterInactive = (node) => {
    if (!node || !node.is_active) return null;
    return {
        ...node,
        subordinates: (node.subordinates || []).map(filterInactive).filter(Boolean),
    };
};

const hasActiveDescendants = (node) => {
    if (!node?.subordinates) return false;
    return node.subordinates.some((c) => c.is_active || hasActiveDescendants(c));
};

const countActiveDescendants = (node) => {
    if (!node?.subordinates) return 0;
    let n = 0;
    for (const c of node.subordinates) {
        if (c.is_active) n += 1;
        n += countActiveDescendants(c);
    }
    return n;
};

const collectActiveEmployeeIds = (root) => {
    const ids = new Set();
    const walk = (n) => {
        if (!n) return;
        if (n.is_active && n.employee?.id) ids.add(n.employee.id);
        (n.subordinates || []).forEach(walk);
    };
    walk(root);
    return ids;
};

// Re-shape a backend subtree into the PUT payload shape (matches add_subordinates
// in emedhrmsapi/configuration/views/subordinates.py).
const toPayloadNode = (node) => ({
    id: node.node_id,
    card: node.card?.id,
    employee: node.employee?.id,
    is_admin: !!node.is_admin,
    is_manager: !!node.is_manager,
    is_active: !!node.is_active,
    subordinates: (node.subordinates || []).map(toPayloadNode),
});

// Sentinel id for the virtual "Organization" parent we inject when there's
// more than one super-admin root. d3-org-chart wraps d3.stratify which
// requires exactly one parentless node, so a synthetic root is the cleanest
// way to render multiple super admins side by side.
const VIRTUAL_ROOT_ID = "__org_root__";

// Node template fed to d3-org-chart. Returns an HTML string (the library
// injects it). Keep all dynamic colors inline so dark mode + custom card
// colours render consistently.
const nodeTemplate = (d, canManage = false) => {
    // Virtual "Organization" parent — short, neutral, no actions.
    if (d.data.id === VIRTUAL_ROOT_ID) {
        return `
          <div data-node-id="${VIRTUAL_ROOT_ID}" style="
            width: ${d.width}px;
            height: ${d.height}px;
            padding: 0;
            box-sizing: border-box;
            background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
            color: #ffffff;
            border-radius: 10px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
            display: flex; align-items: center; justify-content: center;
            font-weight: 600;
            font-size: 14px;
            letter-spacing: 0.3px;
          ">
            Organization
          </div>
        `;
    }
    const n = d.data._node;
    const colour = ensureHash(n?.card?.colour) || "#9ca3af";
    const cardTitle = n?.card?.title || "";
    const emp = n?.employee || {};
    const name = `${emp.title || ""} ${emp.first_name || emp.employee_name || ""} ${emp.last_name || ""}`.trim();
    const designation = emp.designation_id?.[0]?.designation_name
        || emp.designation?.name
        || emp.designation?.designation_name
        || "";
    const department = emp.department_id?.[0]?.department_name
        || emp.department?.name
        || emp.department?.department_name
        || "";
    const empCode = emp.employee_id || emp.id || "";
    const isManager = !!n?.is_manager;
    const isAdmin = !!n?.is_admin;
    const inactive = !n?.is_active;

    // Root cannot be dragged (you can't reparent the CEO). Everyone else can
    // be dragged as a subtree; the container handlers below validate cycles
    // and prompt for confirmation.
    const draggable = !isAdmin && canManage;

    // Action UI (pencil edit + Add/Lead/Remove row) is only rendered when the
    // user has manage permission on Subordinate setup.
    const pencilBtn = canManage
        ? `<button data-action="edit" title="Change employee" class="org-node-edit" style="position:absolute; top:34px; right:6px; z-index:5; width:24px; height:24px; display:flex; align-items:center; justify-content:center; background:#ffffff; border:1px solid #e5e7eb; border-radius:9999px; box-shadow:0 1px 2px rgba(0,0,0,0.15); cursor:pointer; color:#4f46e5; padding:0;"><svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"></path></svg></button>`
        : "";

    const actionRow = canManage
        ? `<div style="display:flex; align-items:stretch; border-top: 1px solid #f3f4f6;" class="dark:!border-gray-700">
          <button data-action="add" style="flex:1; padding:6px; font-size: 11px; background: transparent; border: none; cursor: pointer; color: #4f46e5;">+ Add</button>
          ${isManager
            ? '<button data-action="unlead" style="flex:1; padding:6px; font-size: 11px; background: transparent; border-left: 1px solid #f3f4f6; border-right: 1px solid #f3f4f6; cursor: pointer; color: #b45309;">Unset Lead</button>'
            : '<button data-action="lead" style="flex:1; padding:6px; font-size: 11px; background: transparent; border-left: 1px solid #f3f4f6; border-right: 1px solid #f3f4f6; cursor: pointer; color: #0ea5e9;">Make Lead</button>'
        }
          <button data-action="remove" style="flex:1; padding:6px; font-size: 11px; background: transparent; border: none; cursor: pointer; color: #dc2626;">Remove</button>
        </div>`
        : "";

    return `
      <div data-node-id="${d.data.id}" ${draggable ? 'draggable="true"' : ""} style="
        width: ${d.width}px;
        height: ${d.height}px;
        padding: 0;
        box-sizing: border-box;
        background: #ffffff;
        position: relative;
        border-radius: 10px;
        box-shadow: 0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04);
        overflow: hidden;
        ${inactive ? "opacity: 0.45;" : ""}
        ${draggable ? "cursor: grab;" : ""}
        border: 1px solid #e5e7eb;
        font-family: inherit;
      " class="org-node dark:!bg-gray-800 dark:!border-gray-700">
        ${pencilBtn}
        <div style="
          background: ${colour};
          color: #ffffff;
          padding: 6px 10px;
          font-size: 12px;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: space-between;
          letter-spacing: 0.2px;
        ">
          <span style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
            ${cardTitle || "—"}
          </span>
          <span style="display:flex; align-items:center; gap:4px;">
            ${isAdmin ? '<span title="Root" style="background:rgba(255,255,255,0.25); padding:1px 6px; border-radius:9999px; font-size:10px;">ROOT</span>' : ""}
            ${isManager ? '<span title="Team Lead" style="background:rgba(255,255,255,0.25); padding:1px 6px; border-radius:9999px; font-size:10px;">LEAD</span>' : ""}
          </span>
        </div>
        <div style="padding: 10px 12px; display:flex; flex-direction: column; gap: 2px;">
          <div style="font-size: 13px; font-weight: 600; color:#111827; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;" class="dark:!text-gray-100">
            ${name || "Unassigned"}
          </div>
          <div style="font-size: 11px; color:#6b7280; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;" class="dark:!text-gray-400">
            ${designation || "—"}${department ? ` · ${department}` : ""}
          </div>
          <div style="font-size: 10px; color:#9ca3af; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
            ${empCode || ""}
          </div>
        </div>
        ${actionRow}
      </div>
    `;
};

// ============================================================================
// Cards CRUD modal — title + colour swatch picker. Reuses the existing
// DialogModal shell so it matches the rest of the app's UX.
// ============================================================================
const CardManageModal = ({ open, onClose, onSaved, cards }) => {
    const { create, update } = useApiMethods();
    const [editing, setEditing] = useState(null);
    const [title, setTitle] = useState("");
    const [colour, setColour] = useState(COLOR_PALETTE[0]);
    const [saving, setSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    useEffect(() => {
        if (!open) {
            setEditing(null);
            setTitle("");
            setColour(COLOR_PALETTE[0]);
            setSaving(false);
            setDeleteTarget(null);
        }
    }, [open]);

    const beginEdit = (card) => {
        setEditing(card);
        setTitle(card.title || "");
        setColour(ensureHash(card.colour) || COLOR_PALETTE[0]);
    };

    const handleSave = async () => {
        if (!title.trim()) {
            ToastMsg("error", "Card title is required");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                title: title.trim(),
                colour: colour.startsWith("#") ? colour.slice(1) : colour,
                is_active: true,
            };
            const res = editing
                ? await update(SUBORDINATE_CARDS, { ...payload, id: editing.id })
                : await create(SUBORDINATE_CARDS, payload);
            if (res?.status === "success") {
                ToastMsg("success", res.message || (editing ? "Card updated" : "Card added"));
                onSaved?.();
                setEditing(null);
                setTitle("");
                setColour(COLOR_PALETTE[0]);
            } else {
                ToastMsg("error", res?.message || "Failed");
            }
        } catch (_) {
            ToastMsg("error", "Failed to save card");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            // Backend soft-deletes via PUT with is_active=false (no DELETE method
            // is exposed on /configuration/cards/). Match the legacy behavior.
            const res = await update(SUBORDINATE_CARDS, {
                id: deleteTarget.id,
                title: deleteTarget.title,
                colour: deleteTarget.colour,
                is_active: false,
            });
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Card removed");
                setDeleteTarget(null);
                onSaved?.();
            } else {
                ToastMsg("error", res?.message || "Failed");
            }
        } catch (_) {
            ToastMsg("error", "Failed to remove card");
        }
    };

    return (
        <>
            <DialogModal
                open={open}
                onClose={onClose}
                onSave={handleSave}
                title="Manage Cards"
                saveLabel={editing ? "Update Card" : "Add Card"}
                loading={saving}
                width="560px"
                editMode={!!editing}
            >
                <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Card Title <span className="text-red-500">*</span>
                            </label>
                            <Input
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="e.g. CEO, Manager, Tech Lead"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Colour</label>
                            <div className="flex items-center gap-2 flex-wrap">
                                {COLOR_PALETTE.map((c) => (
                                    <button
                                        key={c}
                                        type="button"
                                        onClick={() => setColour(c)}
                                        title={c}
                                        style={{ backgroundColor: c }}
                                        className={`w-7 h-7 rounded-full border-2 transition-transform ${
                                            colour.toLowerCase() === c.toLowerCase()
                                                ? "border-gray-900 dark:border-white scale-110"
                                                : "border-transparent hover:scale-105"
                                        }`}
                                    />
                                ))}
                                <input
                                    type="color"
                                    value={ensureHash(colour)}
                                    onChange={(e) => setColour(e.target.value)}
                                    className="w-7 h-7 rounded cursor-pointer border border-gray-300 dark:border-gray-600 bg-transparent"
                                />
                            </div>
                        </div>
                    </div>

                    <div>
                        <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Existing Cards</div>
                        <div className="rounded-md border border-gray-200 dark:border-gray-700 max-h-56 overflow-auto">
                            {cards.length === 0 ? (
                                <div className="p-4 text-center text-sm text-muted-foreground">No cards yet — create one above.</div>
                            ) : (
                                <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                                    {cards.map((c) => (
                                        <li key={c.id} className="flex items-center gap-2 px-3 py-2">
                                            <span
                                                className="w-4 h-4 rounded-full flex-shrink-0 border border-gray-300 dark:border-gray-600"
                                                style={{ backgroundColor: ensureHash(c.colour) }}
                                            />
                                            <span className="text-sm flex-1 truncate text-gray-900 dark:text-gray-100">{c.title}</span>
                                            <Button size="sm" variant="ghost" onClick={() => beginEdit(c)} title="Edit">
                                                <Edit className="w-4 h-4" />
                                            </Button>
                                            <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(c)} title="Remove" className="text-red-500 hover:text-red-600">
                                                <Trash className="w-4 h-4" />
                                            </Button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            </DialogModal>

            <ConfirmPopupModal
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                title="Remove Card"
                description={`Remove the card "${deleteTarget?.title}"? Nodes using it will keep their colour until reassigned.`}
                type="danger"
                destructive
                confirmLabel="Remove"
            />
        </>
    );
};

// ============================================================================
// Add Employee dialog — shown when the user clicks "+ Add" on a node or starts
// a fresh tree. Filters out employees already in the chart.
// ============================================================================
const AddEmployeeDialog = ({
    open, onClose, onConfirm, employees, cards,
    departments, designations, busy,
    title,
}) => {
    const [card, setCard] = useState(undefined);
    const [employee, setEmployee] = useState(undefined);
    const [department, setDepartment] = useState(undefined);
    const [designation, setDesignation] = useState(undefined);

    useEffect(() => {
        if (!open) {
            setCard(undefined);
            setEmployee(undefined);
            setDepartment(undefined);
            setDesignation(undefined);
        }
    }, [open]);

    const filteredEmployees = useMemo(() => {
        return employees.filter((e) => {
            const empDept = e.department_id?.[0]?.id || e.department?.id;
            const empDesig = e.designation_id?.[0]?.id || e.designation?.id;
            const deptOk = !department || empDept === department;
            const desigOk = !designation || empDesig === designation;
            return deptOk && desigOk;
        });
    }, [employees, department, designation]);

    const handleSave = () => {
        if (!card) { ToastMsg("error", "Pick a card"); return; }
        if (!employee) { ToastMsg("error", "Pick an employee"); return; }
        onConfirm?.({ card, employee });
    };

    return (
        <DialogModal
            open={open}
            onClose={onClose}
            onSave={handleSave}
            title={title || "Add Employee"}
            saveLabel="Add"
            loading={busy}
            width="540px"
        >
            <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Department</label>
                        <Select
                            value={department}
                            onChange={setDepartment}
                            options={departments}
                            placeholder="All"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            className="w-full"
                            size="large"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Designation</label>
                        <Select
                            value={designation}
                            onChange={setDesignation}
                            options={designations}
                            placeholder="All"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            className="w-full"
                            size="large"
                        />
                    </div>
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Employee <span className="text-red-500">*</span>
                    </label>
                    <Select
                        value={employee}
                        onChange={setEmployee}
                        options={filteredEmployees.map((e) => {
                            const name = `${e.first_name || e.employee_name || ""} ${e.last_name || ""}`.trim();
                            const code = e.employee_id || e.id;
                            return { value: e.id, label: `${name} (${code})` };
                        })}
                        placeholder={filteredEmployees.length === 0 ? "No employees available" : "Pick an employee"}
                        showSearch
                        optionFilterProp="label"
                        className="w-full"
                        size="large"
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Card <span className="text-red-500">*</span>
                    </label>
                    <Select
                        value={card}
                        onChange={setCard}
                        options={cards.map((c) => ({
                            value: c.id,
                            label: (
                                <span className="inline-flex items-center gap-2">
                                    <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: ensureHash(c.colour) }} />
                                    {c.title}
                                </span>
                            ),
                        }))}
                        placeholder="Pick a card / role"
                        className="w-full"
                        size="large"
                        showSearch
                        optionFilterProp="label"
                    />
                </div>
            </div>
        </DialogModal>
    );
};

// ============================================================================
// Replace-Employee dialog — shown when the user tries to remove a node that
// still has active subordinates. They MUST pick a replacement (or cancel).
// ============================================================================
const ReplaceEmployeeDialog = ({
    open, onClose, onConfirm, onSaveRole, target, employees, cards,
    departments, designations, busy, childCount,
}) => {
    const [employee, setEmployee] = useState(undefined);
    const [card, setCard] = useState(undefined);
    const [department, setDepartment] = useState(undefined);
    const [designation, setDesignation] = useState(undefined);
    // "edit"  = change this node's card / role only (default entry from pencil)
    // "replace" = swap the occupying employee (reveals the employee picker)
    const [mode, setMode] = useState("edit");

    useEffect(() => {
        if (!open) {
            setEmployee(undefined);
            setCard(undefined);
            setDepartment(undefined);
            setDesignation(undefined);
            setMode("edit");
        } else if (target) {
            // Preselect the existing card so an edit keeps the same role label
            // unless the user explicitly changes it; always start in edit mode.
            setCard(target.card?.id);
            setEmployee(undefined);
            setDepartment(undefined);
            setDesignation(undefined);
            setMode("edit");
        }
    }, [open, target]);

    const filteredEmployees = useMemo(() => {
        return employees.filter((e) => {
            const empDept = e.department_id?.[0]?.id || e.department?.id;
            const empDesig = e.designation_id?.[0]?.id || e.designation?.id;
            const deptOk = !department || empDept === department;
            const desigOk = !designation || empDesig === designation;
            return deptOk && desigOk;
        });
    }, [employees, department, designation]);

    const handleSave = () => {
        if (!card) { ToastMsg("error", "Pick a card / role"); return; }
        if (mode === "replace") {
            if (!employee) { ToastMsg("error", "Pick a replacement employee"); return; }
            onConfirm?.({ employee, card });
        } else {
            onSaveRole?.({ card });
        }
    };

    const currentName = target?.employee?.first_name
        || target?.employee?.employee_name
        || "this employee";

    // Details of the employee currently occupying this node — shown at the top
    // so the pencil dialog reads as an "edit this person's card/role" view.
    const curEmp = target?.employee || {};
    const currentFullName = `${curEmp.title || ""} ${curEmp.first_name || curEmp.employee_name || ""} ${curEmp.last_name || ""}`.trim();
    const currentDesignation = curEmp.designation_id?.[0]?.designation_name
        || curEmp.designation?.name
        || curEmp.designation?.designation_name
        || "";
    const currentDepartment = curEmp.department_id?.[0]?.department_name
        || curEmp.department?.name
        || curEmp.department?.department_name
        || "";
    const currentCode = curEmp.employee_id || curEmp.id || "";

    return (
        <DialogModal
            open={open}
            onClose={onClose}
            onSave={handleSave}
            title={mode === "replace" ? "Replace Employee" : "Edit Card / Role"}
            saveLabel={mode === "replace" ? "Confirm Replacement" : "Save"}
            loading={busy}
            width="560px"
        >
            <div className="space-y-3">
                {target && (
                    <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-3">
                        <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                            Current employee
                        </p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {currentFullName || "Unassigned"}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            {currentDesignation || "—"}{currentDepartment ? ` · ${currentDepartment}` : ""}
                        </p>
                        {currentCode && (
                            <p className="text-[11px] text-gray-400 dark:text-gray-500">{currentCode}</p>
                        )}
                        {target.card?.title && (
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                                Current role: <span className="font-medium">{target.card.title}</span>
                            </p>
                        )}
                    </div>
                )}

                {mode === "edit" && (
                    <button
                        type="button"
                        onClick={() => setMode("replace")}
                        className="inline-flex items-center gap-1 text-xs font-medium text-sidebar-primary hover:underline"
                    >
                        Replace employee instead →
                    </button>
                )}

                {mode === "replace" && (<>
                {childCount > 0 && (
                <div className="rounded-md border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/20 p-3 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 dark:text-amber-300 flex-shrink-0" />
                    <p className="text-xs text-amber-700 dark:text-amber-300 leading-relaxed">
                        <span className="font-medium">{currentName}</span> has <span className="font-medium">{childCount} active report{childCount === 1 ? "" : "s"}</span>.
                        Reassigning this position keeps those reports attached to the same node — just pick the replacement employee below.
                    </p>
                </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Department</label>
                        <Select
                            value={department}
                            onChange={setDepartment}
                            options={departments}
                            placeholder="All"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            className="w-full"
                            size="large"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Designation</label>
                        <Select
                            value={designation}
                            onChange={setDesignation}
                            options={designations}
                            placeholder="All"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            className="w-full"
                            size="large"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Replacement Employee <span className="text-red-500">*</span>
                    </label>
                    <Select
                        value={employee}
                        onChange={setEmployee}
                        options={filteredEmployees.map((e) => {
                            const name = `${e.first_name || e.employee_name || ""} ${e.last_name || ""}`.trim();
                            const code = e.employee_id || e.id;
                            return { value: e.id, label: `${name} (${code})` };
                        })}
                        placeholder={filteredEmployees.length === 0 ? "No available employees" : "Pick a replacement"}
                        showSearch
                        optionFilterProp="label"
                        className="w-full"
                        size="large"
                    />
                </div>
                </>)}

                <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Card / Role <span className="text-red-500">*</span>
                    </label>
                    <Select
                        value={card}
                        onChange={setCard}
                        options={cards.map((c) => ({
                            value: c.id,
                            label: (
                                <span className="inline-flex items-center gap-2">
                                    <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: ensureHash(c.colour) }} />
                                    {c.title}
                                </span>
                            ),
                        }))}
                        className="w-full"
                        size="large"
                        showSearch
                        optionFilterProp="label"
                    />
                </div>
            </div>
        </DialogModal>
    );
};

// ============================================================================
// Main page — manages d3-org-chart instance, action menus, save flow.
// ============================================================================
const SubordinateSetup = () => {
    const { fetch: apiFetch, update } = useApiMethods();

    // Permission gate — org-chart editing (add / lead / remove / replace /
    // reparent / card CRUD) is treated as a single "manage" capability.
    const { add: addPermission, edit: editPermission } =
        checkUserPermissionFunc("configuration", "Subordinate", "SubMenu");
    const canManage = !!(addPermission || editPermission);

    const containerRef = useRef(null);
    const chartRef = useRef(null);

    const [roots, setRoots] = useState([]);            // forest — one tree per super admin
    const [orphans, setOrphans] = useState([]);        // active employees with no RM and not super-admin
    const [cards, setCards] = useState([]);            // active cards only
    const [allEmployees, setAllEmployees] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [cardModalOpen, setCardModalOpen] = useState(false);
    const [addEmployee, setAddEmployee] = useState({ open: false, parentNode: null, mode: "child" });
    const [confirmRemove, setConfirmRemove] = useState(null);
    // Replacement flow — fires when the user tries to remove a node that still
    // has active reports. We force them to pick a replacement so the team
    // doesn't get orphaned visually.
    const [replaceTarget, setReplaceTarget] = useState(null);
    // Drag-and-drop reparent confirmation.
    const [movePending, setMovePending] = useState(null); // { source, target }

    // Track currently in-chart employees so the dropdown doesn't offer them again.
    const usedEmployeeIds = useMemo(() => {
        const set = new Set();
        for (const r of roots) {
            for (const id of collectActiveEmployeeIds(r)) set.add(id);
        }
        return set;
    }, [roots]);

    const availableEmployees = useMemo(
        () => allEmployees.filter((e) => !usedEmployeeIds.has(e.id) && e.is_active !== false),
        [allEmployees, usedEmployeeIds],
    );

    // For the "Seed Super-Admin Root" flow we constrain the picker to active
    // employees flagged `is_admin=true`. Anyone else would be rejected by the
    // backend anyway — filter on the client too so HR sees only valid options.
    const availableAdmins = useMemo(
        () => availableEmployees.filter((e) => e.is_admin === true),
        [availableEmployees],
    );

    // ---------- Fetch helpers ----------
    const loadTree = useCallback(async () => {
        try {
            const res = await apiFetch(SUBORDINATE_SETUP);
            const data = res?.data;
            // Backend now returns an array of root trees (one per super admin),
            // plus an `orphans` list of active employees who currently have no
            // reporting manager and aren't super-admins themselves.
            const list = Array.isArray(data) ? data : (data ? [data] : []);
            setRoots(list);
            setOrphans(res?.orphans || []);
        } catch (_) {
            setRoots([]);
            setOrphans([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadCards = useCallback(async () => {
        try {
            const res = await apiFetch(SUBORDINATE_CARDS);
            const list = (res?.data || []).filter((c) => c.is_active !== false);
            setCards(list);
        } catch (_) { setCards([]); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadMeta = useCallback(async () => {
        try {
            const [empRes, deptRes, desigRes] = await Promise.all([
                apiFetch(EMPLOYEE_PERSONAL_INFO, { drop_down: true }),
                apiFetch(EMPLOYEE_DEPARTMENT, { drop_down: true }),
                apiFetch(EMPLOYEE_DESIGNATION, { drop_down: true }),
            ]);
            setAllEmployees(empRes?.data || []);
            setDepartments((deptRes?.data || []).map((d) => ({ value: d.id, label: d.department_name })));
            setDesignations((desigRes?.data || []).map((d) => ({ value: d.id, label: d.designation_name })));
        } catch (_) { /* non-fatal */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setLoading(true);
        Promise.all([loadTree(), loadCards(), loadMeta()]).finally(() => setLoading(false));
    }, [loadTree, loadCards, loadMeta]);

    // ---------- d3-org-chart mount + update ----------
    useEffect(() => {
        if (!containerRef.current) return;
        if (!chartRef.current) {
            chartRef.current = new OrgChart();
        }
        // Filter out soft-deleted branches and flatten every root into one
        // list. d3-org-chart uses d3.stratify internally which only accepts
        // a single parentless node — so when there are multiple super-admin
        // roots we splice a synthetic "Organization" parent above them. The
        // virtual node has no employee, no actions and is filtered out of
        // every mutation path.
        const visibleRoots = roots.map(filterInactive).filter(Boolean);
        const rawFlat = visibleRoots.flatMap((r) => flattenTree(r));
        // Final dedup pass across the entire forest — flattenTree already
        // dedupes within a single tree, but the same employee can leak in
        // across two roots if the backend's data is inconsistent (e.g. a
        // super admin who also has a reporting_manager set, so they appear
        // both as a root AND as a child). d3.stratify would crash with
        // "ambiguous: <id>" if we let duplicates through.
        const seenIds = new Set();
        const dropped = [];
        const flat = [];
        for (const row of rawFlat) {
            if (!row.id || row.id === "undefined" || row.id === "null") continue;
            if (seenIds.has(row.id)) { dropped.push(row.id); continue; }
            seenIds.add(row.id);
            flat.push(row);
        }
        if (dropped.length) {
            // Surface the inconsistency so QA can spot it. The chart still
            // renders correctly because the duplicate is filtered out.
            // eslint-disable-next-line no-console
            console.warn(
                "[OrgChart] dropped duplicate node ids:", dropped,
                "— check Employee.reporting_manager for inconsistencies.",
            );
        }
        // Reparent check — any row whose parentId points to a non-existent id
        // would crash d3.stratify too. Reset such orphans to the empty string
        // (root) and we'll wrap them under the virtual root just like any
        // other top-level node.
        for (const row of flat) {
            if (row.parentId && !seenIds.has(row.parentId)) {
                row.parentId = "";
            }
        }
        // d3.stratify allows exactly one parentless node. Count them
        // post-orphan-handling — if there are 2+ we wrap them under the
        // virtual "Organization" root so the chart still renders.
        const parentlessCount = flat.reduce(
            (n, r) => (r.parentId === "" ? n + 1 : n), 0,
        );
        if (parentlessCount > 1) {
            flat.forEach((row) => {
                if (row.parentId === "") row.parentId = VIRTUAL_ROOT_ID;
            });
            flat.unshift({
                id: VIRTUAL_ROOT_ID,
                parentId: "",
                _node: { __virtual: true },
            });
        }
        if (flat.length === 0) {
            // Clear the canvas if there's no data — d3-org-chart doesn't render
            // an empty list well; we'll show our own empty state via overlay.
            containerRef.current.innerHTML = "";
            chartRef.current = new OrgChart();
            return;
        }
        chartRef.current
            .container(containerRef.current)
            .data(flat)
            .nodeWidth(() => 240)
            .nodeHeight(() => 130)
            .childrenMargin(() => 60)
            .siblingsMargin(() => 24)
            .compactMarginBetween(() => 24)
            .compactMarginPair(() => 60)
            .nodeContent((d) => nodeTemplate(d, canManage))
            .render();
        // Keep the whole hierarchy expanded — the user explicitly wants every
        // level visible after any state change, instead of d3-org-chart's
        // default "only first level expanded" behavior.
        chartRef.current.expandAll();

        // Wire node-level action clicks. d3-org-chart re-renders on every
        // .data().render() so we re-bind here every effect run.
        const root = containerRef.current;
        const onClick = (ev) => {
            const btn = ev.target.closest("button[data-action]");
            if (!btn) return;
            if (!canManage) return; // defensive: no mutations without permission
            const wrap = btn.closest("[data-node-id]");
            if (!wrap) return;
            const nodeId = wrap.getAttribute("data-node-id");
            const action = btn.getAttribute("data-action");
            const node = findNodeInForest(roots, nodeId);
            if (!node) return;
            ev.stopPropagation();
            if (action === "edit") {
                setReplaceTarget(node);
            } else if (action === "add") {
                if (!node.is_manager) {
                    ToastMsg("info", "Mark this person as a Team Lead before adding subordinates");
                    return;
                }
                setAddEmployee({ open: true, parentNode: node, mode: "child" });
            } else if (action === "remove") {
                // If this node still has active subordinates, refuse the plain
                // remove and ask the user to pick a replacement so the chain
                // doesn't break.
                if (hasActiveDescendants(node)) {
                    setReplaceTarget(node);
                } else {
                    setConfirmRemove(node);
                }
            } else if (action === "lead") {
                mutateNode(nodeId, (n) => ({ ...n, is_manager: true }), "add_as_a_team_lead", "Marked as Team Lead");
            } else if (action === "unlead") {
                mutateNode(nodeId, (n) => ({ ...n, is_manager: false }), "remove_as_a_team_lead", "Unset as Team Lead");
            }
        };
        // --- Drag and drop (HTML5) -----------------------------------------
        // d3-org-chart has no native reparent API, so we hook the standard
        // drag events on the custom node template. The source nodeId travels
        // through dataTransfer; on drop we open a confirm modal and the
        // actual tree mutation runs through the existing full-tree PUT path.
        //
        // d3-zoom (used internally by d3-org-chart for pan/zoom) binds its
        // pan handler to the SVG mousedown. The SVG sits between the node
        // template and our container, so by default a mousedown on a card
        // triggers a pan instead of a drag. We swallow mousedown/pointerdown
        // in capture phase when the target is a draggable card — d3-zoom
        // never sees the event, so no pan starts. The browser still detects
        // the drag gesture because it watches the `draggable` attribute
        // directly. Empty-space mousedown still bubbles through and pans.
        let dragSourceId = null;

        const blockPanOnCard = (ev) => {
            const wrap = ev.target.closest('[data-node-id][draggable="true"]');
            if (wrap) ev.stopPropagation();
        };

        const onDragStart = (ev) => {
            const wrap = ev.target.closest("[data-node-id]");
            if (!wrap || wrap.getAttribute("draggable") !== "true") return;
            dragSourceId = wrap.getAttribute("data-node-id");
            try {
                ev.dataTransfer.setData("text/plain", dragSourceId);
                ev.dataTransfer.effectAllowed = "move";
            } catch (_) { /* Firefox sometimes throws if dataTransfer is read-only */ }
            wrap.style.opacity = "0.5";
        };

        const onDragEnd = (ev) => {
            const wrap = ev.target.closest("[data-node-id]");
            if (wrap) wrap.style.opacity = "";
            // Clear any drop-target highlights we left behind.
            root.querySelectorAll("[data-drop-hover='1']").forEach((el) => {
                el.style.outline = "";
                el.removeAttribute("data-drop-hover");
            });
            dragSourceId = null;
        };

        const onDragOver = (ev) => {
            if (!dragSourceId) return;
            const wrap = ev.target.closest("[data-node-id]");
            if (!wrap) return;
            const tgtId = wrap.getAttribute("data-node-id");
            if (tgtId === dragSourceId) return;
            if (tgtId === VIRTUAL_ROOT_ID) return; // virtual node isn't a real drop target
            ev.preventDefault();
            ev.dataTransfer.dropEffect = "move";
            if (wrap.getAttribute("data-drop-hover") !== "1") {
                wrap.style.outline = "3px dashed #4f46e5";
                wrap.style.outlineOffset = "2px";
                wrap.setAttribute("data-drop-hover", "1");
            }
        };

        const onDragLeave = (ev) => {
            const wrap = ev.target.closest("[data-node-id]");
            if (wrap && wrap.getAttribute("data-drop-hover") === "1") {
                wrap.style.outline = "";
                wrap.removeAttribute("data-drop-hover");
            }
        };

        const onDrop = (ev) => {
            const wrap = ev.target.closest("[data-node-id]");
            if (!wrap) return;
            const tgtId = wrap.getAttribute("data-node-id");
            const srcId = dragSourceId || ev.dataTransfer.getData("text/plain");
            if (!srcId || srcId === tgtId) return;
            // Virtual root isn't a valid drop target — drops on it are no-ops.
            if (tgtId === VIRTUAL_ROOT_ID || srcId === VIRTUAL_ROOT_ID) return;
            ev.preventDefault();
            wrap.style.outline = "";
            wrap.removeAttribute("data-drop-hover");

            const source = findNodeInForest(roots, srcId);
            const target = findNodeInForest(roots, tgtId);
            if (!source || !target) return;

            // Cycle guard — can't drop a node into one of its own descendants.
            if (findNodeByNodeId(source, tgtId)) {
                ToastMsg("error", "Cannot move a manager under their own report");
                return;
            }
            // Roots themselves are super admins; we don't let HR shove one
            // under another via drag (use the admin module to demote first).
            if (source.is_admin) {
                ToastMsg("error", "Super admins cannot be dragged under another node");
                return;
            }
            setMovePending({ source, target });
        };

        root.addEventListener("click", onClick);
        root.addEventListener("dragstart", onDragStart);
        root.addEventListener("dragend", onDragEnd);
        root.addEventListener("dragover", onDragOver);
        root.addEventListener("dragleave", onDragLeave);
        root.addEventListener("drop", onDrop);
        // Capture phase so we beat d3-zoom's SVG-level mousedown handler.
        root.addEventListener("mousedown", blockPanOnCard, true);
        root.addEventListener("pointerdown", blockPanOnCard, true);
        return () => {
            root.removeEventListener("click", onClick);
            root.removeEventListener("dragstart", onDragStart);
            root.removeEventListener("dragend", onDragEnd);
            root.removeEventListener("dragover", onDragOver);
            root.removeEventListener("dragleave", onDragLeave);
            root.removeEventListener("drop", onDrop);
            root.removeEventListener("mousedown", blockPanOnCard, true);
            root.removeEventListener("pointerdown", blockPanOnCard, true);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roots]);

    // ---------- Mutations ----------
    // All save paths go through the same PUT — we just shape the body to
    // match the backend's add_subordinates contract.
    const putSubordinate = async (payload, successMsg) => {
        setSaving(true);
        try {
            const res = await update(SUBORDINATE_SETUP, payload);
            if (res?.status === "success") {
                ToastMsg("success", res.message || successMsg);
                await loadTree();
            } else {
                ToastMsg("error", res?.message || "Failed to save");
            }
        } catch (_) {
            ToastMsg("error", "Failed to save");
        } finally {
            setSaving(false);
        }
    };

    // Apply a mutator to one node and PUT the affected root only. Every
    // in-place edit (lead toggle, remove, add-subordinate) flows through here
    // so the backend's recursive _apply sees the complete super-admin →
    // target parent chain and can set reporting_manager_id on every row.
    const mutateNode = (targetNodeId, mutator, action, successMsg) => {
        const idx = findOwningRootIndex(roots, targetNodeId);
        if (idx < 0) {
            ToastMsg("error", "Could not locate the target node");
            return Promise.resolve();
        }
        const updatedRoot = replaceNodeInTree(roots[idx], targetNodeId, mutator);
        if (!updatedRoot) {
            ToastMsg("error", "Could not locate the target node");
            return Promise.resolve();
        }
        return putSubordinate({ ...toPayloadNode(updatedRoot), action }, successMsg);
    };

    // Seed a root tree. The selected employee must already be a super admin
    // (Employee.is_admin=True); we never promote a regular employee from the
    // chart UI. The dialog's employee picker is already filtered to admins,
    // and the backend rejects non-admin roots as a defence-in-depth.
    const handleAddRoot = ({ card, employee }) => {
        const payload = {
            card,
            employee,
            is_admin: true,
            is_manager: true,
            is_active: true,
            subordinates: [],
            action: "add_employee",
        };
        putSubordinate(payload, "Root employee added")
            .then(() => setAddEmployee({ open: false, parentNode: null, mode: "child" }));
    };

    // Add a child to a manager. Mutates the parent in the full tree, then
    // PUTs the whole thing — same pattern as the legacy implementation.
    const handleAddChild = ({ card, employee }) => {
        const parent = addEmployee.parentNode;
        if (!parent) return;
        // Shape the new child to match the GET tree shape so toPayloadNode
        // picks it up: undefined node_id becomes no `id` in the payload, which
        // makes the backend create a fresh row.
        const newChild = {
            node_id: undefined,
            card: { id: card },
            employee: { id: employee },
            is_admin: false,
            is_manager: false,
            is_active: true,
            subordinates: [],
        };
        mutateNode(
            parent.node_id,
            (n) => ({ ...n, subordinates: [...(n.subordinates || []), newChild] }),
            "add_employee",
            "Employee added",
        ).then(() => setAddEmployee({ open: false, parentNode: null, mode: "child" }));
    };

    const handleRemove = () => {
        if (!confirmRemove) return;
        mutateNode(
            confirmRemove.node_id,
            (n) => ({ ...n, is_active: false }),
            "remove_employee",
            "Employee removed",
        ).then(() => setConfirmRemove(null));
    };

    // Reparent a node (with its full subtree) under a new manager. Source and
    // target may live in different super-admin roots — that's fine, we only
    // PUT the destination root because the backend's recursive _apply sets
    // reporting_manager_id on every node it sees, so the moved node's RM gets
    // rewired correctly regardless of where it used to live.
    const handleMoveNode = () => {
        if (!movePending) return;
        const { source, target } = movePending;
        const dstIdx = findOwningRootIndex(roots, target.node_id);
        if (dstIdx < 0) {
            ToastMsg("error", "Could not locate the destination root");
            setMovePending(null);
            return;
        }
        const srcIdx = findOwningRootIndex(roots, source.node_id);
        const sameRoot = srcIdx === dstIdx;

        // Detach the source from its root (could be the destination root, in
        // which case detach is just a local rearrangement before re-insertion).
        const srcRoot = roots[srcIdx];
        const { detached, remainingTree } = detachNode(srcRoot, source.node_id);
        if (!detached) {
            ToastMsg("error", "Could not detach the source node");
            setMovePending(null);
            return;
        }
        // The destination root we operate on: when source and target share a
        // root we work from `remainingTree` so the source isn't there twice.
        const dstRoot = sameRoot ? remainingTree : roots[dstIdx];
        const newDst = replaceNodeInTree(dstRoot, target.node_id, (n) => ({
            ...n,
            is_manager: true, // auto-promote the new parent so it can host reports
            subordinates: [...(n.subordinates || []), detached],
        }));
        if (!newDst) {
            ToastMsg("error", "Could not locate the drop target");
            setMovePending(null);
            return;
        }
        putSubordinate(
            { ...toPayloadNode(newDst), action: "add_employee" },
            "Moved to new manager",
        ).then(() => setMovePending(null));
    };

    // Replace the person occupying this node with another employee. We keep
    // the same node_id so every child's reporting_manager_id (which points to
    // this row) stays valid — the team is preserved, only the head changes.
    const handleReplace = ({ employee, card }) => {
        if (!replaceTarget) return;
        mutateNode(
            replaceTarget.node_id,
            (n) => ({
                ...n,
                employee: { ...(n.employee || {}), id: employee },
                card: card ? { id: card } : n.card,
                is_active: true,
            }),
            "add_employee",
            "Replacement assigned",
        ).then(() => setReplaceTarget(null));
    };

    // Change only the card / role of this node — the occupying employee stays.
    const handleEditRole = ({ card }) => {
        if (!replaceTarget) return;
        mutateNode(
            replaceTarget.node_id,
            (n) => ({ ...n, card: card ? { id: card } : n.card }),
            "add_employee",
            "Card / role updated",
        ).then(() => setReplaceTarget(null));
    };

    // ---------- Toolbar ----------
    const handleFit = () => chartRef.current?.fit?.();
    const handleExpandAll = () => chartRef.current?.expandAll?.()?.fit?.();

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={Network}
                title="Subordinate Setup"
                subtitle="Visual organization hierarchy. Add subordinates, manage cards and colors."
            />

            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-2 px-3 pb-3">
                {canManage && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCardModalOpen(true)}
                        className="gap-1"
                    >
                        <Filter className="w-4 h-4" /> Manage Cards ({cards.length})
                    </Button>
                )}
                {canManage && roots.length === 0 && !loading && (
                    <Button
                        size="sm"
                        className="bg-sidebar hover:bg-sidebar-primary text-white gap-1"
                        onClick={() => setAddEmployee({ open: true, parentNode: null, mode: "root" })}
                    >
                        <Plus className="w-4 h-4" /> Seed Super-Admin Root
                    </Button>
                )}
                <div className="ml-auto flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={handleExpandAll} className="gap-1">
                        <Maximize2 className="w-4 h-4" /> Expand All
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleFit} className="gap-1">
                        Fit to Screen
                    </Button>
                </div>
            </div>

            {/* Chart canvas */}
            <div className="px-3 pb-6">
                <div className="relative rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 overflow-hidden" style={{ height: "calc(100vh - 220px)" }}>
                    {loading ? (
                        <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
                            Loading organization chart…
                        </div>
                    ) : roots.length === 0 ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center">
                            <Users className="w-10 h-10 text-muted-foreground" />
                            <div>
                                <p className="text-base font-medium text-gray-900 dark:text-gray-100">No super-admin roots yet</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    The chart draws every employee whose Employee.is_admin = true as a root.
                                    Create a card first if you haven't, then pick a super-admin employee to seed it.
                                </p>
                            </div>
                            {canManage && (
                                <div className="flex gap-2">
                                    {cards.length === 0 ? (
                                        <Button size="sm" onClick={() => setCardModalOpen(true)} className="bg-sidebar hover:bg-sidebar-primary text-white">
                                            Create First Card
                                        </Button>
                                    ) : (
                                        <Button size="sm" onClick={() => setAddEmployee({ open: true, parentNode: null, mode: "root" })} className="bg-sidebar hover:bg-sidebar-primary text-white">
                                            <Plus className="w-4 h-4 mr-1" /> Seed Super-Admin Root
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : null}
                    <style>{`
                        .org-node .org-node-edit { opacity: 0; transition: opacity 0.15s ease; }
                        .org-node:hover .org-node-edit { opacity: 1; }
                    `}</style>
                    <div ref={containerRef} className="w-full h-full" />
                    {saving && (
                        <div className="absolute top-2 right-2 px-2 py-1 rounded bg-black/60 text-white text-xs">Saving…</div>
                    )}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                    Click <span className="font-medium">+ Add</span> on any Team Lead to add a subordinate.
                    Use <span className="font-medium">Make Lead / Unset Lead</span> to control whether a node can have reports.
                    <span className="hidden sm:inline"> Drag and drop a card onto another card to reparent under a new manager.</span>
                </p>

                {orphans.length > 0 && (
                    <div className="mt-4 rounded-md border border-amber-200 dark:border-amber-900/40 bg-amber-50/60 dark:bg-amber-900/20 p-3 sm:p-4">
                        <div className="flex items-start gap-2 mb-2">
                            <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 dark:text-amber-300 flex-shrink-0" />
                            <div className="min-w-0">
                                <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                                    Unassigned employees ({orphans.length})
                                </p>
                                <p className="text-xs text-amber-700 dark:text-amber-300">
                                    Active employees with no reporting manager. Pick one to place them under a Team Lead.
                                </p>
                            </div>
                        </div>
                        <ul className="flex flex-wrap gap-2 mt-2">
                            {orphans.map((emp) => (
                                <li key={emp.id} className="inline-flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md px-2 py-1 text-xs">
                                    <span className="font-medium text-gray-900 dark:text-gray-100">{emp.employee_name || emp.id}</span>
                                    {emp.designation?.name && (
                                        <span className="text-muted-foreground">· {emp.designation.name}</span>
                                    )}
                                    {emp.department?.name && (
                                        <span className="text-muted-foreground">· {emp.department.name}</span>
                                    )}
                                </li>
                            ))}
                        </ul>
                        <p className="text-[11px] text-amber-700/80 dark:text-amber-300/80 mt-2">
                            Tip: open any Team Lead's <span className="font-medium">+ Add</span> menu — these employees will appear in the picker.
                        </p>
                    </div>
                )}
            </div>

            {/* Cards manage modal */}
            <CardManageModal
                open={cardModalOpen}
                onClose={() => setCardModalOpen(false)}
                onSaved={loadCards}
                cards={cards}
            />

            {/* Add-employee dialog */}
            <AddEmployeeDialog
                open={addEmployee.open}
                onClose={() => setAddEmployee({ open: false, parentNode: null, mode: "child" })}
                onConfirm={addEmployee.mode === "root" ? handleAddRoot : handleAddChild}
                employees={addEmployee.mode === "root" ? availableAdmins : availableEmployees}
                cards={cards}
                departments={departments}
                designations={designations}
                busy={saving}
                title={addEmployee.mode === "root" ? "Seed Super-Admin Root" : `Add Subordinate to ${addEmployee.parentNode?.employee?.first_name || addEmployee.parentNode?.employee?.employee_name || "this lead"}`}
            />

            {/* Confirm remove (only for nodes with no active reports) */}
            <ConfirmPopupModal
                open={!!confirmRemove}
                onClose={() => setConfirmRemove(null)}
                onConfirm={handleRemove}
                title="Remove from Org Chart"
                description={`Remove ${confirmRemove?.employee?.first_name || confirmRemove?.employee?.employee_name || "this employee"} from the hierarchy?`}
                type="danger"
                destructive
                confirmLabel="Remove"
            />

            {/* Drag-and-drop reparent confirmation */}
            <ConfirmPopupModal
                open={!!movePending}
                onClose={() => setMovePending(null)}
                onConfirm={handleMoveNode}
                title="Move under New Manager"
                description={movePending ? (() => {
                    const srcName = movePending.source?.employee?.first_name || movePending.source?.employee?.employee_name || "this person";
                    const tgtName = movePending.target?.employee?.first_name || movePending.target?.employee?.employee_name || "the target";
                    const reports = countActiveDescendants(movePending.source);
                    const trail = reports > 0 ? ` along with their ${reports} report${reports === 1 ? "" : "s"}` : "";
                    const promote = movePending.target?.is_manager ? "" : ` ${tgtName} will be promoted to Team Lead.`;
                    return `Move ${srcName}${trail} under ${tgtName}?${promote}`;
                })() : ""}
                type="info"
                confirmLabel="Move"
            />

            {/* Replace dialog — opens when removing a node that has active reports */}
            <ReplaceEmployeeDialog
                open={!!replaceTarget}
                onClose={() => setReplaceTarget(null)}
                onConfirm={handleReplace}
                onSaveRole={handleEditRole}
                target={replaceTarget}
                childCount={replaceTarget ? countActiveDescendants(replaceTarget) : 0}
                employees={availableEmployees}
                cards={cards}
                departments={departments}
                designations={designations}
                busy={saving}
            />
        </ContainerCard>
    );
};

export default SubordinateSetup;
